/**
 * Device Owner crash/OOM self-heal bridge (issue #1369).
 *
 * Native AlarmManager relaunches the player when `last_foreground_at` goes stale. JS must keep
 * writing the heartbeat while foreground, and suppress relaunch during the field-service unlock window.
 */
import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { getPlatform } from '@/platform'

export interface TAndroidProcessSelfHealStatus {
  deviceOwner: boolean
  lastForegroundAt: number
  restartAttemptsInWindow: number
  lastRestartAt: number
  maintenanceSuppressUntil: number
  alarmIntervalMs: number
  staleThresholdMs: number
}

/** How often the foreground loop may ping native (well under the native ~4 min stale threshold). */
export const PROCESS_SELF_HEAL_HEARTBEAT_INTERVAL_MS = 30_000

let lastHeartbeatAtMs = 0
let cachedStatus: TAndroidProcessSelfHealStatus | null = null

function parseStatus(raw: unknown): TAndroidProcessSelfHealStatus | null {
  if (raw == null || typeof raw !== 'object') {
    return null
  }
  const o = raw as Record<string, unknown>
  if (
    typeof o.deviceOwner !== 'boolean'
    || typeof o.lastForegroundAt !== 'number'
    || typeof o.restartAttemptsInWindow !== 'number'
    || typeof o.lastRestartAt !== 'number'
    || typeof o.maintenanceSuppressUntil !== 'number'
    || typeof o.alarmIntervalMs !== 'number'
    || typeof o.staleThresholdMs !== 'number'
  ) {
    return null
  }
  return {
    deviceOwner: o.deviceOwner,
    lastForegroundAt: o.lastForegroundAt,
    restartAttemptsInWindow: o.restartAttemptsInWindow,
    lastRestartAt: o.lastRestartAt,
    maintenanceSuppressUntil: o.maintenanceSuppressUntil,
    alarmIntervalMs: o.alarmIntervalMs,
    staleThresholdMs: o.staleThresholdMs
  }
}

async function invokeSelfHealCommand(
  command: 'recordProcessHeartbeat' | 'setMaintenanceSuppressUntil' | 'getProcessSelfHealStatus',
  args?: Record<string, unknown>
): Promise<TAndroidProcessSelfHealStatus | null> {
  if (!isTauriRuntime() || getPlatform() !== 'android') {
    return null
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    const raw = args == null
      ? await invoke<unknown>(`plugin:lucimark-android|${command}`)
      : await invoke<unknown>(`plugin:lucimark-android|${command}`, args)
    const status = parseStatus(raw)
    if (status != null) {
      cachedStatus = status
    }
    return status
  }
  catch {
    return null
  }
}

/** Last status returned by a successful native call (for diagnostics snapshot). */
export function getCachedAndroidProcessSelfHealStatus(): TAndroidProcessSelfHealStatus | null {
  return cachedStatus
}

/**
 * Records a foreground heartbeat when due. Safe on web / non-Android / non-DO (native no-ops schedule).
 * Call from the Android watchdog ping loop while the document is visible.
 */
export async function recordAndroidProcessHeartbeat(nowMs = Date.now()): Promise<TAndroidProcessSelfHealStatus | null> {
  // lastHeartbeatAtMs === 0 means never sent; do not treat epoch as a recent ping.
  if (lastHeartbeatAtMs > 0 && nowMs - lastHeartbeatAtMs < PROCESS_SELF_HEAL_HEARTBEAT_INTERVAL_MS) {
    return cachedStatus
  }
  lastHeartbeatAtMs = nowMs
  return invokeSelfHealCommand('recordProcessHeartbeat')
}

/** Suppress self-heal relaunch until `untilMs` (field-service PIN unlock window). */
export async function setAndroidMaintenanceSuppressUntil(untilMs: number): Promise<TAndroidProcessSelfHealStatus | null> {
  return invokeSelfHealCommand('setMaintenanceSuppressUntil', { untilMs })
}

export async function getAndroidProcessSelfHealStatus(): Promise<TAndroidProcessSelfHealStatus | null> {
  return invokeSelfHealCommand('getProcessSelfHealStatus')
}

/** Test helper. */
export function resetAndroidProcessSelfHealStateForTests(): void {
  lastHeartbeatAtMs = 0
  cachedStatus = null
}
